import React, { useRef } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BalanceBucketFilter, BalanceBucketsDto } from '../../../../common/interfaces/invoicing.interface';
import { formatMoney } from '../../../../utilities/helper/money.helper';

const CARDS: { bucket: BalanceBucketFilter; dot: string; label: string }[] = [
  { bucket: 'all', dot: 'text.primary', label: 'balances.cards.all' },
  { bucket: 'late30', dot: 'error.main', label: 'balances.cards.late30' },
  { bucket: 'late', dot: 'warning.main', label: 'balances.cards.late' },
  { bucket: 'notdue', dot: 'field.border', label: 'balances.cards.notdue' },
  { bucket: 'credit', dot: 'info.main', label: 'balances.cards.credit' }
];

/**
 * The debts by how urgent they are, as a filter: choosing a card lists only its children.
 *
 * The figures ignore the card chosen - they are counted over the search and group only -
 * so a card never changes its number because another one was picked, and always in
 * children, in the payer view too.
 */
const BalanceCards: React.FC<{
  buckets: BalanceBucketsDto | null;
  value: BalanceBucketFilter;
  onChange: (bucket: BalanceBucketFilter) => void;
}> = ({ buckets, value, onChange }) => {
  const { t } = useTranslation();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const move = (from: number, delta: number) => {
    const next = (from + delta + CARDS.length) % CARDS.length;
    onChange(CARDS[next].bucket);
    refs.current[next]?.focus();
  };

  return (
    <Box
      role="radiogroup"
      aria-label={t('balances.cards.ariaLabel')}
      sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(4, 1fr) 200px' }, gap: 2, mb: 3 }}
    >
      {CARDS.map((card, index) => {
        const selected = card.bucket === value;
        const figures = buckets?.[card.bucket] ?? { amount: 0, count: 0 };
        const isCredit = card.bucket === 'credit';
        return (
          <ButtonBase
            key={card.bucket}
            ref={element => {
              refs.current[index] = element;
            }}
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(card.bucket)}
            onKeyDown={event => {
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                event.preventDefault();
                move(index, 1);
              } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                event.preventDefault();
                move(index, -1);
              }
            }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              textAlign: 'left',
              gap: 0.5,
              borderRadius: 4,
              border: 1,
              borderColor: 'line',
              p: '16px 20px',
              ...(selected ? { borderWidth: 2, borderColor: 'primary.main', bgcolor: 'surface.tint', p: '15px 19px' } : {}),
              '&:hover': selected ? undefined : { borderColor: 'field.hover' },
              '&.Mui-focusVisible': { outline: 2, outlineStyle: 'solid', outlineColor: 'primary.main', outlineOffset: 2 }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box aria-hidden sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: card.dot, flexShrink: 0 }} />
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{t(card.label)}</Typography>
            </Box>
            <Typography
              sx={{
                fontSize: isCredit ? 18 : 22,
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
                color: card.bucket === 'late30' && figures.amount > 0 ? 'error.main' : 'text.primary'
              }}
            >
              {formatMoney(figures.amount)}
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {figures.count ? t('balances.cards.children', { count: figures.count }) : t('balances.cards.nobody')}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
};

export default BalanceCards;
